'use client';

import { useEffect, useSyncExternalStore } from 'react';

export type IosTrackingStatus =
  | 'authorized'
  | 'denied'
  | 'notDetermined'
  | 'restricted';

/**
 * - `not-ios`: web or Android — the in-page consent banner applies.
 * - `unsupported`: an iOS build that predates the ATT plugin. Apple does not
 *   accept a web banner as tracking permission, so tracking stays off.
 * - `supported`: Apple's App Tracking Transparency prompt is the consent.
 */
export type IosTrackingSupport = 'not-ios' | 'unsupported' | 'supported';

interface StatusResponse {
  status: IosTrackingStatus;
}

interface AppTrackingTransparencyPlugin {
  getStatus(): Promise<StatusResponse>;
  requestPermission(): Promise<StatusResponse>;
}

interface PluginListenerHandle {
  remove(): Promise<void> | void;
}

interface AppPlugin {
  addListener(
    event: 'appStateChange',
    handler: (state: { isActive: boolean }) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: {
    App?: AppPlugin;
    AppTrackingTransparency?: AppTrackingTransparencyPlugin;
  };
}

const PLUGIN_NAME = 'AppTrackingTransparency';
// Lets the splash fade out first: iOS silently drops the request while the
// app is not active, and a prompt over the splash reads as part of launch.
const REQUEST_DELAY_MS = 800;

let currentStatus: IosTrackingStatus | null = null;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function getCapacitor(): CapacitorGlobal | undefined {
  if (typeof window === 'undefined') return undefined;

  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function getIosTrackingSupport(): IosTrackingSupport {
  const cap = getCapacitor();

  if (!cap?.isNativePlatform?.() || cap.getPlatform?.() !== 'ios') {
    return 'not-ios';
  }

  return cap.isPluginAvailable?.(PLUGIN_NAME) &&
    cap.Plugins?.AppTrackingTransparency
    ? 'supported'
    : 'unsupported';
}

function subscribeToSupport() {
  return () => {};
}

function getServerSupport(): IosTrackingSupport {
  return 'not-ios';
}

/** Hydration-safe read: the server renders as web, the client corrects. */
export function useIosTrackingSupport() {
  return useSyncExternalStore(
    subscribeToSupport,
    getIosTrackingSupport,
    getServerSupport,
  );
}

function setStatus(status: IosTrackingStatus) {
  if (status === currentStatus) return;

  currentStatus = status;
  listeners.forEach((listener) => listener());
}

/**
 * Reads the current status and, while it is still undetermined, asks once.
 * Re-run on every return to the foreground: that both picks up a change made
 * in iOS Settings and retries a request iOS dropped (e.g. because another
 * system alert such as the push prompt was on screen).
 */
function syncTrackingStatus() {
  const plugin = getCapacitor()?.Plugins?.AppTrackingTransparency;

  if (!plugin || inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { status } = await plugin.getStatus();

      if (
        status !== 'notDetermined' ||
        document.visibilityState !== 'visible'
      ) {
        setStatus(status);
        return;
      }

      setStatus((await plugin.requestPermission()).status);
    } catch {
      setStatus('notDetermined');
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return currentStatus;
}

function getServerSnapshot() {
  return null;
}

/**
 * Returns the ATT status (null until first read). Only prompts when
 * `enabled` — i.e. the tenant has a marketing tag switched on — so tenants
 * without ad tags never show the tracking prompt.
 */
export function useIosTrackingStatus(enabled: boolean) {
  const status = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  // Subscribing to the native app lifecycle has no React-state equivalent.
  useEffect(() => {
    if (!enabled || getIosTrackingSupport() !== 'supported') return;

    const timer = window.setTimeout(syncTrackingStatus, REQUEST_DELAY_MS);
    const app = getCapacitor()?.Plugins?.App;
    const handle: { cancelled: boolean; remove: (() => void) | null } = {
      cancelled: false,
      remove: null,
    };

    if (app) {
      Promise.resolve(
        app.addListener('appStateChange', ({ isActive }) => {
          if (isActive) void syncTrackingStatus();
        }),
      )
        .then((listener) => {
          if (handle.cancelled) {
            void listener.remove();
          } else {
            handle.remove = () => void listener.remove();
          }
        })
        .catch(() => {
          // Foreground re-sync is best effort; the launch read still ran.
        });
    }

    return () => {
      window.clearTimeout(timer);
      handle.cancelled = true;
      handle.remove?.();
    };
  }, [enabled]);

  return status;
}

/**
 * Opens this app's page in iOS Settings, where the "Allow Tracking" toggle
 * lives. Capacitor hands non-app URLs to UIApplication.open.
 */
export function openIosAppSettings() {
  window.location.href = 'app-settings:';
}
